import { useState } from "react";
import { FolderPlus, TagIcon, BookOpen, FolderClosed, Inbox, Trash2, Download, X, Plus, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { paletteToken } from "@/lib/palette";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TAG_COLORS = [
  { name: "stone", cls: "bg-stone-200 text-stone-800 dark:bg-stone-700 dark:text-stone-100" },
  { name: "burgundy", cls: "bg-[#FCE4F0] text-[#C9286F] dark:bg-[#4A1D3A] dark:text-[#F9A8D4]" },
  { name: "forest", cls: "bg-[#EDE7FE] text-[#8B5CF6] dark:bg-[#2E2150] dark:text-[#C4B5FD]" },
  { name: "ochre", cls: "bg-[#F5E4C7] text-[#8C6321] dark:bg-[#3b2e14] dark:text-[#e9c081]" },
  { name: "slate", cls: "bg-[#DFE4EA] text-[#3A4657] dark:bg-[#1e252e] dark:text-[#a0b0c4]" },
];

export function tagColorClasses(color) {
  return TAG_COLORS.find((c) => c.name === color)?.cls || TAG_COLORS[0].cls;
}

export default function Sidebar({
  folders,
  tags,
  citations,
  selectedFolder,
  setSelectedFolder,
  selectedTagIds,
  setSelectedTagIds,
  selectedKeywords = [],
  setSelectedKeywords,
  onCreateFolder,
  onDeleteFolder,
  onCreateTag,
  onDeleteTag,
  onExportAll,
  onManageKeywords,
  keywordColor = () => "forest",
}) {
  const [folderName, setFolderName] = useState("");
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState("burgundy");
  const [addingFolder, setAddingFolder] = useState(false);
  const [addingTag, setAddingTag] = useState(false);

  const unfiledCount = citations.filter((c) => !c.folder_id).length;

  // Build keyword frequency map
  const keywordCounts = (() => {
    const map = new Map();
    for (const c of citations) {
      const seen = new Set();
      for (const k of c.keywords || []) {
        const key = k.trim();
        if (!key || seen.has(key.toLowerCase())) continue;
        seen.add(key.toLowerCase());
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    return [...map.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  })();
  const maxKw = keywordCounts[0]?.count || 1;
  const kwSize = (n) => {
    if (maxKw <= 1) return "text-xs";
    const ratio = n / maxKw;
    if (ratio > 0.75) return "text-sm font-semibold";
    if (ratio > 0.4) return "text-xs font-semibold";
    return "text-xs";
  };

  const toggleKeyword = (name) => {
    if (!setSelectedKeywords) return;
    setSelectedKeywords((prev) =>
      prev.map((x) => x.toLowerCase()).includes(name.toLowerCase())
        ? prev.filter((x) => x.toLowerCase() !== name.toLowerCase())
        : [...prev, name]
    );
  };

  const toggleTag = (id) =>
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  return (
    <aside
      data-testid="sidebar"
      className="w-72 shrink-0 hidden md:flex flex-col border-r border-[#F5D0E3] dark:border-[#45325A] bg-[#FDF2F8] dark:bg-[#1A1224] h-screen sticky top-0"
    >
      <div className="p-5 flex-1 overflow-y-auto">
        {/* Library section */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#7A6A8C] dark:text-[#8E7BA6] mb-2 px-2">
            Library
          </div>
          <SidebarItem
            testId="folder-all"
            active={selectedFolder === "all"}
            onClick={() => setSelectedFolder("all")}
            icon={<BookOpen className="w-4 h-4" />}
            label="All citations"
            count={citations.length}
          />
          <SidebarItem
            testId="folder-unfiled"
            active={selectedFolder === "unfiled"}
            onClick={() => setSelectedFolder("unfiled")}
            icon={<Inbox className="w-4 h-4" />}
            label="Unfiled"
            count={unfiledCount}
          />
        </div>

        {/* Projects/Folders */}
        <div className="mb-6" data-testid="sidebar-folder-list">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#7A6A8C] dark:text-[#8E7BA6]">
              Projects
            </div>
            <button
              data-testid="create-folder-button"
              onClick={() => setAddingFolder((v) => !v)}
              className="text-[#7A6A8C] hover:text-[#C9286F] dark:hover:text-[#C4A1FF] transition-colors"
              aria-label="Add project"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
          </div>

          {addingFolder && (
            <div className="mb-2 px-2 flex gap-1 fade-slide-in">
              <Input
                data-testid="new-folder-input"
                autoFocus
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && folderName.trim()) {
                    onCreateFolder(folderName.trim());
                    setFolderName("");
                    setAddingFolder(false);
                  }
                  if (e.key === "Escape") setAddingFolder(false);
                }}
                placeholder="Project name…"
                className="h-8 text-xs bg-white dark:bg-[#3B2350]"
              />
              <button
                onClick={() => {
                  if (folderName.trim()) {
                    onCreateFolder(folderName.trim());
                    setFolderName("");
                    setAddingFolder(false);
                  }
                }}
                className="text-[#C9286F] dark:text-[#C4A1FF]"
                data-testid="save-folder-button"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}

          {folders.map((f) => {
            const count = citations.filter((c) => c.folder_id === f.id).length;
            return (
              <div key={f.id} className="group flex items-center gap-1">
                <SidebarItem
                  testId={`folder-${f.id}`}
                  active={selectedFolder === f.id}
                  onClick={() => setSelectedFolder(f.id)}
                  icon={<FolderClosed className="w-4 h-4" />}
                  label={f.name}
                  count={count}
                  flex
                />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`delete-folder-${f.id}`}
                      className="opacity-0 group-hover:opacity-100 text-[#7A6A8C] hover:text-[#C9286F] p-1 transition-opacity"
                      aria-label="Delete project"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete project {f.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Citations in this project will be moved to Unfiled. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDeleteFolder(f.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            );
          })}
          {folders.length === 0 && !addingFolder && (
            <div className="text-xs text-[#7A6A8C] px-2 py-1">No projects yet</div>
          )}
        </div>

        {/* Tags */}
        <div className="mb-6" data-testid="sidebar-tag-list">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#7A6A8C] dark:text-[#8E7BA6]">
              Tags
            </div>
            <button
              data-testid="create-tag-button"
              onClick={() => setAddingTag((v) => !v)}
              className="text-[#7A6A8C] hover:text-[#C9286F] dark:hover:text-[#C4A1FF] transition-colors"
              aria-label="Add tag"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {addingTag && (
            <div className="mb-2 px-2 space-y-2 fade-slide-in">
              <Input
                data-testid="new-tag-input"
                autoFocus
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tagName.trim()) {
                    onCreateTag(tagName.trim(), tagColor);
                    setTagName("");
                    setAddingTag(false);
                  }
                  if (e.key === "Escape") setAddingTag(false);
                }}
                placeholder="Tag name…"
                className="h-8 text-xs bg-white dark:bg-[#3B2350]"
              />
              <div className="flex gap-1">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setTagColor(c.name)}
                    className={`w-5 h-5 rounded-full ${c.cls.split(" ")[0]} border ${tagColor === c.name ? "ring-2 ring-[#C9286F] ring-offset-1 dark:ring-offset-[#1A1224]" : "border-transparent"}`}
                    aria-label={c.name}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 px-2">
            {tags.map((t) => {
              const active = selectedTagIds.includes(t.id);
              return (
                <div key={t.id} className="group flex items-center">
                  <button
                    data-testid={`tag-filter-${t.id}`}
                    onClick={() => toggleTag(t.id)}
                    className={`text-xs px-2 py-1 rounded-full transition-all ${tagColorClasses(t.color)} ${active ? "ring-2 ring-[#C9286F] dark:ring-[#C4A1FF]" : "opacity-90 hover:opacity-100"}`}
                  >
                    {t.name}
                  </button>
                  <button
                    data-testid={`delete-tag-${t.id}`}
                    onClick={() => onDeleteTag(t.id)}
                    className="opacity-0 group-hover:opacity-100 text-[#7A6A8C] hover:text-[#C9286F] ml-0.5 transition-opacity"
                    aria-label="Delete tag"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
            {tags.length === 0 && !addingTag && (
              <div className="text-xs text-[#7A6A8C]">No tags yet</div>
            )}
          </div>
        </div>

        {/* Keywords cloud */}
        <div className="mb-6" data-testid="sidebar-keyword-cloud">
          <div className="flex items-center justify-between mb-2 px-2">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#7A6A8C] dark:text-[#8E7BA6] flex items-center gap-1.5">
              <Hash className="w-3 h-3" /> Keywords
            </div>
            {keywordCounts.length > 0 && (
              <button
                data-testid="manage-keywords-button"
                onClick={onManageKeywords}
                className="text-xs uppercase tracking-wide text-[#C9286F] dark:text-[#C4A1FF] hover:underline"
              >
                Manage
              </button>
            )}
          </div>
          {keywordCounts.length === 0 ? (
            <div className="text-xs text-[#7A6A8C] px-2 py-1">
              Add keywords inside any citation to build your cloud
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 px-2 items-baseline">
              {keywordCounts.slice(0, 40).map((k) => {
                const active = selectedKeywords
                  .map((x) => x.toLowerCase())
                  .includes(k.name.toLowerCase());
                const tok = paletteToken(keywordColor(k.name));
                return (
                  <button
                    key={k.name}
                    data-testid={`keyword-chip-${k.name}`}
                    onClick={() => toggleKeyword(k.name)}
                    title={`${k.count} citation${k.count === 1 ? "" : "s"}`}
                    className={`${kwSize(k.count)} px-2 py-0.5 rounded-full transition-all border`}
                    style={
                      active
                        ? { backgroundColor: tok.hex, color: "#ffffff", borderColor: tok.hex }
                        : { backgroundColor: tok.bg, color: tok.fg, borderColor: "transparent" }
                    }
                  >
                    #{k.name}
                    <span className="ml-1 opacity-60 text-[9px] font-normal">{k.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 border-t border-[#F5D0E3] dark:border-[#45325A]">
        <Button
          data-testid="export-bibtex-all-button"
          onClick={onExportAll}
          variant="outline"
          className="w-full gap-2 border-[#F5D0E3] dark:border-[#45325A] bg-white dark:bg-[#3B2350] hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140]"
        >
          <Download className="w-4 h-4" /> Export BibTeX
        </Button>
      </div>
    </aside>
  );
}

function SidebarItem({ testId, active, onClick, icon, label, count, flex }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`${flex ? "flex-1" : "w-full"} flex items-center justify-between text-left px-3 py-2 rounded-md text-sm transition-colors ${
        active
          ? "bg-white dark:bg-[#2E2140] text-[#C9286F] dark:text-[#C4A1FF] shadow-sm font-medium"
          : "text-[#5E4B70] dark:text-[#BBA9CF] hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140]"
      }`}
    >
      <span className="flex items-center gap-2 truncate">
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <span className={`text-xs ${active ? "text-[#C9286F] dark:text-[#C4A1FF]" : "text-[#7A6A8C]"}`}>{count}</span>
    </button>
  );
}
